import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { ShieldCheck, ArrowsClockwise, CheckCircle, Warning, CalendarStar, Plus, Info } from "@phosphor-icons/react";

const HR_ROLES = new Set(["super_admin", "company_admin", "hr"]);

const TYPE_META = {
  PF:   { name: "Provident Fund", tagline: "12% employee + 12% employer", regulator: "EPFO" },
  ESIC: { name: "Employees' State Insurance", tagline: "0.75% employee + 3.25% employer", regulator: "ESIC Corp" },
  PT:   { name: "Professional Tax", tagline: "State-level (varies by state)", regulator: "State Govt" },
  TDS:  { name: "Tax Deducted at Source", tagline: "Slab-based deduction", regulator: "Income Tax Dept" },
  LWF:  { name: "Labour Welfare Fund", tagline: "State-level small deduction", regulator: "State LWF Board" },
};

const TONE_MAP = {
  green: { bg: "bg-zenith-green/10 border-zenith-green/40 text-zenith-green", chip: "bg-zenith-green text-white" },
  amber: { bg: "bg-amber-50 border-amber-200 text-amber-700", chip: "bg-amber-500 text-white" },
  red:   { bg: "bg-red-50 border-red-200 text-red-700", chip: "bg-red-500 text-white" },
};

export default function StatCompliance() {
  const { user } = useAuth();
  const isAdmin = HR_ROLES.has(user?.role);
  const [status, setStatus] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [{ data: s }, { data: c }, { data: h }] = await Promise.all([
        api.get("/pmo/stat-compliance/status"),
        api.get("/pmo/stat-compliance/calendar"),
        api.get("/pmo/stat-compliance/history"),
      ]);
      setStatus(s); setCalendar(c); setHistory(h);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const scoreboard = useMemo(() => ({
    onTrack: status.filter((s) => s.tone === "green").length,
    dueSoon: status.filter((s) => s.tone === "amber").length,
    overdue: status.filter((s) => s.tone === "red").length,
  }), [status]);

  return (
    <div className="space-y-6" data-testid="stat-compliance-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zenith-teal font-bold">Statutory compliance</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Filed. On time. Every time.</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">PF, ESIC, Professional Tax, TDS and Labour Welfare Fund — one dashboard, colour-coded status, and due-date alerts so nothing slips.</p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-sm" data-testid="stat-refresh">
          <ArrowsClockwise size={14} className={busy ? "animate-spin mr-1" : "mr-1"} /> Refresh
        </Button>
      </div>

      {/* Scoreboard */}
      <div className="grid grid-cols-3 gap-3">
        <ScoreCard label="On track" value={scoreboard.onTrack} tone="green" icon={CheckCircle} />
        <ScoreCard label="Due within 5 days" value={scoreboard.dueSoon} tone="amber" icon={CalendarStar} />
        <ScoreCard label="Overdue" value={scoreboard.overdue} tone="red" icon={Warning} />
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {status.map((s) => (
          <StatusCard key={s.kind} row={s} onFile={load} isAdmin={isAdmin} />
        ))}
      </div>

      {/* Upcoming */}
      <div className="border border-zinc-200 rounded-md bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
          <CalendarStar size={16} className="text-zenith-magenta" weight="duotone" />
          <div className="font-display font-bold text-sm text-adb-navy">Upcoming filings — next 60 days</div>
        </div>
        <table className="w-full text-sm" data-testid="stat-calendar-table">
          <thead className="bg-zinc-50 text-left">
            <tr className="text-[11px] uppercase tracking-widest text-zinc-500">
              <th className="py-2 px-3">Kind</th>
              <th className="py-2 px-3">For period</th>
              <th className="py-2 px-3">Due date</th>
              <th className="py-2 px-3">Days left</th>
              <th className="py-2 px-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {calendar.map((c, i) => (
              <tr key={i} className="border-t border-zinc-100">
                <td className="py-2 px-3 font-semibold">{c.kind}</td>
                <td className="py-2 px-3 text-xs font-mono-plex">{c.period_month}</td>
                <td className="py-2 px-3 text-xs">{c.due_date}</td>
                <td className="py-2 px-3 font-mono-plex text-xs">
                  <span className={c.days_to_due <= 5 ? "text-red-600 font-bold" : c.days_to_due <= 15 ? "text-amber-600" : "text-zinc-500"}>
                    {c.days_to_due} days
                  </span>
                </td>
                <td className="py-2 px-3">
                  {c.filed ? (
                    <span className="inline-flex items-center gap-1 text-xs text-zenith-green font-semibold"><CheckCircle size={12} weight="fill" /> Filed · {c.reference_no}</span>
                  ) : (
                    <span className="text-xs text-amber-700 font-semibold">Pending</span>
                  )}
                </td>
              </tr>
            ))}
            {calendar.length === 0 && (
              <tr><td colSpan={5} className="py-6 text-center text-zinc-400 text-sm">No upcoming filings in the next 60 days.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* History */}
      <div className="border border-zinc-200 rounded-md bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
          <ShieldCheck size={16} className="text-zenith-teal" weight="duotone" />
          <div className="font-display font-bold text-sm text-adb-navy">Filing history</div>
        </div>
        <table className="w-full text-sm" data-testid="stat-history-table">
          <thead className="bg-zinc-50 text-left">
            <tr className="text-[11px] uppercase tracking-widest text-zinc-500">
              <th className="py-2 px-3">Kind</th>
              <th className="py-2 px-3">Period</th>
              <th className="py-2 px-3">Filed on</th>
              <th className="py-2 px-3">Due</th>
              <th className="py-2 px-3 text-right">Amount</th>
              <th className="py-2 px-3">Reference</th>
              <th className="py-2 px-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.id} className="border-t border-zinc-100">
                <td className="py-2 px-3 font-semibold">{h.kind}</td>
                <td className="py-2 px-3 font-mono-plex text-xs">{h.period_month}</td>
                <td className="py-2 px-3 text-xs">{h.filed_on}</td>
                <td className="py-2 px-3 text-xs text-zinc-500">{h.due_date}</td>
                <td className="py-2 px-3 text-right font-mono-plex text-sm">₹{new Intl.NumberFormat("en-IN").format(h.amount_inr || 0)}</td>
                <td className="py-2 px-3 font-mono-plex text-xs">{h.reference_no}</td>
                <td className="py-2 px-3">
                  <Badge className={`rounded-sm text-[10px] ${
                    h.status === "filed" ? "bg-zenith-green/10 text-zenith-green border-zenith-green/40"
                    : h.status === "overdue" ? "bg-red-50 text-red-700 border-red-200"
                    : "bg-amber-50 text-amber-700 border-amber-200"
                  }`}>{h.status}</Badge>
                </td>
              </tr>
            ))}
            {history.length === 0 && (
              <tr><td colSpan={7} className="py-6 text-center text-zinc-400 text-sm">No filings recorded yet — mark your first PF/ESIC filing on any status card above.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScoreCard({ label, value, tone, icon: Icon }) {
  const t = TONE_MAP[tone];
  return (
    <div className={`border ${t.bg} rounded-md p-4 flex items-center gap-3`}>
      <div className={`w-10 h-10 rounded-full grid place-items-center ${t.chip}`}>
        <Icon size={18} weight="fill" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
        <div className="font-display text-3xl font-bold mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function StatusCard({ row, isAdmin, onFile }) {
  const meta = TYPE_META[row.kind] || { name: row.kind };
  const t = TONE_MAP[row.tone];
  const statusLabel = {
    filed: "Up to date",
    pending: "Filing pending",
    due_soon: "Due in " + row.days_to_due + "d",
    overdue: Math.abs(row.days_to_due) + "d overdue",
  }[row.status] || row.status;

  return (
    <div className={`border ${t.bg} rounded-md p-4 flex flex-col justify-between`} data-testid={`stat-card-${row.kind}`}>
      <div>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-lg tracking-tight">{row.kind}</div>
            <div className="text-[11px] opacity-70 -mt-0.5">{meta.name}</div>
          </div>
          <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-widest ${t.chip}`}>
            {statusLabel}
          </span>
        </div>
        <div className="mt-3 text-[10px] opacity-75">Next due · <span className="font-mono-plex font-bold">{row.upcoming_due_date}</span></div>
        <div className="text-[10px] opacity-60 mt-0.5">{meta.tagline}</div>
        {row.last_filed_period && (
          <div className="mt-2 text-[10px] opacity-75 border-t border-current/20 pt-2">
            Last filed: <span className="font-mono-plex">{row.last_filed_period}</span>
            {row.last_reference_no && <div className="font-mono-plex opacity-70">ref: {row.last_reference_no}</div>}
          </div>
        )}
      </div>
      {isAdmin && row.pending_period && (
        <FileNowDialog kind={row.kind} period={row.pending_period} onFiled={onFile} />
      )}
    </div>
  );
}

function FileNowDialog({ kind, period, onFiled }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    kind, period_month: period, reference_no: "", amount_inr: "",
    filed_on: new Date().toISOString().slice(0, 10), challan_url: "",
  });
  const submit = async () => {
    if (!f.reference_no || !f.amount_inr) { toast.error("Reference # + amount required"); return; }
    try {
      await api.post("/pmo/stat-compliance/filings", { ...f, amount_inr: Number(f.amount_inr) });
      toast.success(`${kind} filing recorded`);
      setOpen(false);
      onFiled();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="mt-3 rounded-sm bg-adb-navy hover:bg-adb-navy-800 text-white text-xs h-7" data-testid={`stat-file-btn-${kind}`}>
          <Plus size={11} className="mr-1" /> Record filing
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid={`stat-file-dialog-${kind}`}>
        <DialogHeader><DialogTitle className="font-display">Record {kind} filing</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-sm p-2 flex items-start gap-2">
            <Info size={13} className="text-zenith-teal mt-0.5 shrink-0" />
            For period <span className="font-mono-plex font-bold">{period}</span>. Paste your challan reference below.
          </div>
          <div>
            <Label className="text-xs">Challan / Reference # *</Label>
            <Input value={f.reference_no} onChange={(e) => setF({ ...f, reference_no: e.target.value })} placeholder="TRRN12345" className="rounded-sm mt-1 font-mono-plex" data-testid="stat-ref-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Amount (INR) *</Label>
              <Input type="number" value={f.amount_inr} onChange={(e) => setF({ ...f, amount_inr: e.target.value })} className="rounded-sm mt-1" data-testid="stat-amount-input" />
            </div>
            <div>
              <Label className="text-xs">Filed on</Label>
              <Input type="date" value={f.filed_on} onChange={(e) => setF({ ...f, filed_on: e.target.value })} className="rounded-sm mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Challan URL / receipt</Label>
            <Input value={f.challan_url} onChange={(e) => setF({ ...f, challan_url: e.target.value })} placeholder="https://..." className="rounded-sm mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="stat-file-submit">Record</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
